import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  TablePagination
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Article as ArticleIcon
} from '@mui/icons-material';
import api from '../../../../services/api';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import ArticleEditModal from '../../../organisms/Dialogs/ArticleEditModal/ArticleEditModal';

const GlobalArticlesPanel = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Paginação via Backend
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce do search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0); // Volta para pag 1 ao pesquisar
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Modais de Ação
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Modal de edição (Reusando o do sistema cliente)
  const [editModalOpen, setEditModalOpen] = useState(false);

  const fetchGlobalArticles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/global-articles', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearch
        }
      });
      if (res.data.success) {
        setArticles(res.data.data);
        setTotalCount(res.data.total);
      }
    } catch (err) {
      console.error('Erro ao carregar artigos globais:', err);
      setError('Erro ao carregar lista global de artigos');
    }
    setLoading(false);
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    fetchGlobalArticles();
  }, [fetchGlobalArticles]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Açôes
  const handleOpenEdit = (article) => {
    setSelectedArticle(article);
    setEditModalOpen(true);
  };

  const handleCloseEdit = (shouldRefresh) => {
    setEditModalOpen(false);
    setSelectedArticle(null);
    if (shouldRefresh) {
       fetchGlobalArticles();
    }
  };

  const handleOpenDelete = (article) => {
    setSelectedArticle(article);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await api.delete(`/articles/${selectedArticle._id}`);
      if (res.data.success) {
        setSuccess('Artigo excluído permanentemente do sistema!');
        setDeleteDialogOpen(false);
        fetchGlobalArticles();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir artigo');
      setTimeout(() => setError(''), 3000);
      setDeleteDialogOpen(false);
    }
    setSaving(false);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} color="text.primary">Base Global de Artigos</Typography>
          <Typography variant="body2" color="text.secondary">
            Visão sem restrições de Tenant para auditoria total dos artigos armazenados.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar">
            <IconButton onClick={fetchGlobalArticles} disabled={loading}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Card sx={{ mb: 4, bgcolor: 'background.paper', borderRadius: 2 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            placeholder="Buscar por Título, Domínio ou URL..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: { xs: '100%', sm: 400 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            }}
          />
        </Box>
        
        <TableContainer sx={{ maxHeight: 700 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell width={60}>Origem</TableCell>
                <TableCell>Título & Link</TableCell>
                <TableCell>Veículo/Data</TableCell>
                <TableCell>Alocado Ao Cliente (User)</TableCell>
                <TableCell>Marca (Workspace)</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && articles.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : articles.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}><Typography color="text.secondary">Nenhum artigo encontrado na base global.</Typography></TableCell></TableRow>
              ) : (
                articles.map((a) => (
                  <TableRow key={a._id} hover>
                    <TableCell>
                      {a.image ? (
                        <Avatar src={a.image} variant="rounded" sx={{ width: 40, height: 40 }} />
                      ) : (
                        <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'grey.300' }}>
                          <ArticleIcon />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ 
                          display: '-webkit-box', 
                          WebkitLineClamp: 2, 
                          WebkitBoxOrient: 'vertical', 
                          overflow: 'hidden',
                          maxWidth: 350
                      }}>
                        {a.title || 'Sem Título Diferenciado'}
                      </Typography>
                      <Typography 
                          variant="caption" 
                          color="primary" 
                          component="a" 
                          href={a.url || a.siteUrl} 
                          target="_blank" 
                          sx={{ textDecoration: 'none', display: 'block', mt: 0.5, maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {a.url || a.siteUrl || 'Link Indisponível'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                        <Chip size="small" label={a.siteName || 'Desconhecido'} color="default" sx={{ mb: 0.5, fontWeight: 500 }} />
                        <Typography variant="caption" display="block" color="text.secondary">
                            {new Date(a.date || a.createdAt).toLocaleString('pt-BR')}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        {a.createdBy ? (
                            <Box>
                                <Typography variant="body2" fontWeight={500}>{a.createdBy.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{a.createdBy.email}</Typography>
                            </Box>
                        ) : (
                            <Typography variant="caption" color="error">Órfão (Sem Dono)</Typography>
                        )}
                    </TableCell>
                    <TableCell>
                        {a.brandId ? (
                            <Chip size="small" label={a.brandId.name} color="info" variant="outlined" />
                        ) : (
                            <Typography variant="caption" color="text.secondary">Global / Sem Marca</Typography>
                        )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar Artigo">
                        <IconButton size="small" onClick={() => handleOpenEdit(a)} disabled={saving}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir Definitivamente">
                        <IconButton size="small" onClick={() => handleOpenDelete(a)} disabled={saving} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Card>

      {/* Editor Modal da aplicação cliente adaptado */}
      {selectedArticle && editModalOpen && (
          <ArticleEditModal
              open={editModalOpen}
              onClose={() => handleCloseEdit(false)}
              onSuccess={() => handleCloseEdit(true)}
              article={selectedArticle}
              mode="edit"
          />
      )}

      {/* Dialog: Delete Confirmation */}
      <PlatformDialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)} 
        maxWidth="xs" 
        fullWidth
      >
        <PlatformDialogTitle
          title="Excluir Artigo?"
          subtitle="Atenção: Ação irreversível."
          icon={<DeleteIcon />}
          onClose={() => setDeleteDialogOpen(false)}
          color="error"
        />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Excluir permanentemente o artigo do banco de dados removerá de todas as métricas do cliente <strong>{selectedArticle?.createdBy?.name}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1.5 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving} sx={{ fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDelete} 
            disabled={saving}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            {saving ? <CircularProgress size={16} color="inherit" /> : 'Confirmar Exclusão'}
          </Button>
        </DialogActions>
      </PlatformDialog>
    </Box>
  );
};

export default GlobalArticlesPanel;
